import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AddCustomColumnResponse, AddRowParams, ITrackerFile, ITrackerColumn } from '../models/tracker-file.model';
import { IAssignee, IFile } from 'src/app/discrepancies/models/discrepancies.model';
import { IUserModel } from 'src/app/users/models/user.model';
import { Observable } from 'rxjs';
import { AbstractControl, ValidationErrors } from '@angular/forms';

const server = '/api';
const endpoint = server + '/tracker';

@Injectable({
  providedIn: 'root'
})
export class TrackerService {
  private http = inject(HttpClient);

  getTrackerFiles(projectID: string) {
    return this.http.get<{ files: ITrackerFile[]; columnMapping: { [key: string]: string }; columns: ITrackerColumn[] }>(
      `${endpoint}/${projectID}/`
    );
  }

  importTracker(projectID: string, body: FormData) {
    return this.http.post<{
      files: ITrackerFile[];
      unmatchedUsers: string[];
      dateColumnsWithNonDateFormat: string[];
      userVisibilityRestricted: string[];
      multiRoleAssignmentFiles: Pick<ITrackerFile, 'name' | 'number'>[];
      columns: ITrackerColumn[];
      columnMapping: { [key: string]: string };
      taskIdToAssignee?: { [taskId: string]: IAssignee };
      hasMultiAssignedFiles?: boolean;
    }>(`${endpoint}/${projectID}/import`, body);
  }

  importMultiWorksheetTracker(projectID: string, body: FormData) {
    return this.http.post<{
      files: ITrackerFile[];
      unmatchedUsers: string[];
      dateColumnsWithNonDateFormat: string[];
      userVisibilityRestricted: string[];
      multiRoleAssignmentFiles: Pick<ITrackerFile, 'name' | 'number'>[];
      columns: ITrackerColumn[];
      columnMapping: { [key: string]: string };
      taskIdToAssignee?: { [taskId: string]: IAssignee };
      hasMultiAssignedFiles?: boolean;
    }>(`${endpoint}/${projectID}/import-multi-worksheet`, body);
  }

  addSystemFileToTracker(projectID: string, fileID: string) {
    return this.http.post<{ file: ITrackerFile }>(`${endpoint}/${projectID}/create`, { fileID });
  }

  setTrackerStatus(projectID: string, trackerFileID: string, status: string) {
    return this.http.post<{ file: ITrackerFile }>(`${endpoint}/${projectID}/set-status`, {
      projectID,
      trackerFileID,
      status
    });
  }

  setTrackerCustomField(
    projectID: string,
    trackerFileID: string,
    key: string,
    value: string | string[],
    type?: 'user' | 'date' | 'string' | 'dropdown' | 'status'
  ) {
    return this.http.post<{ file: ITrackerFile; assigned: IUserModel[] }>(`${endpoint}/${projectID}/set-custom-field`, {
      trackerFileID,
      key,
      value,
      type
    });
  }

  bulkSetTrackerCustomField(
    projectID: string,
    trackerFileIDs: string[],
    updates: {
      [key: string]: string | string[];
    }
  ) {
    return this.http.post<{
      files: ITrackerFile[];
      assigned: IUserModel[];
      userAssignedToTasks: IAssignee;
      assignedTasks: string[];
      filesAssigneeUpdate: Pick<IFile, 'id' | 'assignees'>[];
    }>(`${endpoint}/${projectID}/bulk-set-custom-field`, {
      trackerFileIDs,
      updates
    });
  }

  deleteTrackerFiles(projectID: string, trackerFileIDs: string[]) {
    return this.http.post<{ success: true }>(`${endpoint}/${projectID}/delete-files`, {
      trackerFileIDs
    });
  }

  assignUserToTrackerFile(
    projectID: string,
    trackerFileID: string,
    userIDs: string[],
    userType: 'productionProgrammer' | 'qcProgrammer' | 'statistician',
    projectFileID?: string
  ) {
    return this.http.post<{
      trackerFile: ITrackerFile;
      projectFile: IFile;
      assigned: IUserModel[];
      userAssignedToTasks?: IAssignee;
      assignedTasks?: string[];
    }>(`${endpoint}/${projectID}/assign`, {
      trackerFileID,
      userIDs,
      userType,
      projectFileID
    });
  }

  addCustomColumn(
    projectID: string,
    columnName: string,
    columnType: string,
    options?: string[],
    position?: 'before' | 'after',
    relativeTo?: string,
    tabCategory?: string
  ): Observable<AddCustomColumnResponse> {
    return this.http.post<AddCustomColumnResponse>(`${endpoint}/${projectID}/columns`, {
      name: columnName,
      type: columnType,
      options,
      position,
      relativeTo,
      tabCategory
    });
  }

  addRow(params: AddRowParams): Observable<{ files: ITrackerFile[] }> {
    const { projectID, tlfType, ...rest } = params;
    return this.http.post<{ files: ITrackerFile[] }>(`${endpoint}/${projectID}/rows`, {
      ...rest,
      type: tlfType
    });
  }

  editColumn(
    projectID: string,
    name: string,
    columnType: string,
    options?: string[] | { action: string; value: string; preValue?: string }[],
    isTypeChange?: boolean,
    oldName?: string,
    tabCategory?: string
  ): Observable<{ files: ITrackerFile[]; columns: ITrackerColumn[] }> {
    return this.http.patch<{ files: ITrackerFile[]; columns: ITrackerColumn[] }>(`${endpoint}/${projectID}/edit`, {
      name,
      columnType,
      options,
      isTypeChange,
      oldName,
      tabCategory
    });
  }

  removeRow(projectID: string, fileID: string) {
    return this.http.delete<{ success: true }>(`${endpoint}/${projectID}/tracker-file/${fileID}`);
  }

  removeColumn(
    projectID: string,
    columnName: string,
    tabCategory?: string
  ): Observable<{ columnName: string; columns: ITrackerColumn[]; files: ITrackerFile[] }> {
    return this.http.delete<{ columnName: string; columns: ITrackerColumn[]; files: ITrackerFile[] }>(
      `${endpoint}/${projectID}/columns`,
      {
        body: { columnName, tabCategory }
      }
    );
  }

  updateTrackerFile(projectId: string, fileId: string, update: Partial<ITrackerFile>): Observable<ITrackerFile> {
    return this.http.patch<ITrackerFile>(`${endpoint}/${projectId}/tracker-files/${fileId}`, update);
  }

  renameColumn(
    projectID: string,
    oldColumnName: string,
    newColumnName: string,
    tabCategory?: string
  ): Observable<{ columnName: string; columns: ITrackerColumn[]; files: ITrackerFile[] }> {
    return this.http.put<{ columnName: string; columns: ITrackerColumn[]; files: ITrackerFile[] }>(
      `${endpoint}/${projectID}/columns/rename`,
      {
        oldName: oldColumnName,
        newName: newColumnName,
        tabCategory
      }
    );
  }

  trackerFileExistsValidator(existingNames: string[]) {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = control.value;
      if (!name) return;
      if (existingNames.includes(name)) {
        return { duplicate: true };
      }
      const index = name.lastIndexOf('.');
      if (index !== -1) {
        const baseName = name.slice(0, index);
        if (existingNames.includes(baseName)) {
          return { duplicate: true };
        }
      } else {
        const nameWithDot = name + '.';
        if (existingNames.some((n) => n.startsWith(nameWithDot))) {
          return { duplicate: true };
        }
      }
      return null;
    };
  }
}
